import { Alert, Button, Checkbox, Form, Input, Spin, Tag } from "@arco-design/web-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileSignature,
  LockKeyhole,
  MessageSquareText,
  ShieldCheck,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useState } from "react";

import {
  fetchOrderHandoverContractSignPortal,
  prepareOrderHandoverContractSign,
} from "../repo";
import type {
  OrderHandoverContractSignPortal,
  OrderHandoverContractSignPreparePayload,
} from "../types";
import "./OrderHandoverContractSignPage.css";

type ContractSignFormValues = {
  signerName: string;
  signerPhone: string;
  signerCertificateNo: string;
  agreedToTerms: boolean;
};

/** 面向签约人的独立合同签署页面。 */
export function OrderHandoverContractSignFeaturePage() {
  const { signRecordId = "" } = useParams();
  const [form] = Form.useForm<ContractSignFormValues>();
  const [enteringContract, setEnteringContract] = useState(false);
  const portalQuery = useQuery({
    queryKey: ["order_handover", "contract_sign_portal", signRecordId],
    queryFn: () => fetchOrderHandoverContractSignPortal(signRecordId),
    enabled: signRecordId.length > 0,
    retry: false,
  });
  const signMutation = useMutation({
    mutationFn: (values: OrderHandoverContractSignPreparePayload) =>
      prepareOrderHandoverContractSign(signRecordId, values),
    onMutate() {
      setEnteringContract(true);
    },
    onSuccess(portal) {
      if (portal.providerActionUrl) {
        window.location.assign(portal.providerActionUrl);
        return;
      }
      setEnteringContract(false);
      if (portal.isSign) {
        notifyContractHandoverPage(portal);
      }
    },
    onError() {
      setEnteringContract(false);
    },
  });
  const portal = signMutation.data ?? portalQuery.data;
  const pageError = portalQuery.error ?? signMutation.error;
  const enteringContractPage = enteringContract || signMutation.isPending;

  return (
    <div className="contract-sign-page" aria-busy={enteringContractPage}>
      {enteringContractPage ? (
        <div
          className="contract-sign-transition"
          role="status"
          aria-label="正在进入合同签订页面"
          aria-live="assertive"
        >
          <Spin dot />
          <strong>正在核验身份并打开合同签订页面</strong>
          <span>请勿关闭或刷新当前页面</span>
        </div>
      ) : null}
      <header className="contract-sign-header">
        <div className="contract-sign-header-inner">
          <div className="contract-sign-brand">
            <span className="contract-sign-brand-icon" aria-hidden="true">
              <FileSignature size={22} />
            </span>
            <div>
              <strong>花儿研学合同签署</strong>
              <span>安全身份核验与电子签约</span>
            </div>
          </div>
          {portal ? (
            <Tag color={portal.isSign ? "green" : "arcoblue"}>{portal.signStateText}</Tag>
          ) : null}
        </div>
      </header>

      <main className="contract-sign-main">
        {portalQuery.isLoading ? (
          <div className="contract-sign-loading" aria-label="合同加载中">
            <Spin dot />
            <span>正在读取合同签署信息</span>
          </div>
        ) : null}

        {pageError && !portal ? (
          <section className="contract-sign-error" aria-live="polite">
            <Alert type="error" showIcon content={getContractSignErrorMessage(pageError)} />
          </section>
        ) : null}

        {portal ? (
          <div className="contract-sign-workspace">
            <aside className="contract-sign-summary" aria-label="合同摘要">
              <div className="contract-sign-summary-heading">
                <span>合同摘要</span>
                <strong>{portal.documentName ?? portal.contractNo}</strong>
              </div>
              <dl>
                <ContractSummaryItem label="合同编号" value={portal.contractNo} />
                <ContractSummaryItem
                  label="合同版本"
                  value={portal.contractTemplateType ?? "旅游电子合同"}
                />
                <ContractSummaryItem label="签约服务" value={portal.providerName} />
                <ContractSummaryItem
                  label="通知状态"
                  value={`${portal.notificationChannelText} · ${portal.notificationStateText}`}
                />
              </dl>
              <div className="contract-sign-security-note">
                <ShieldCheck size={18} />
                <span>身份信息仅用于核对本次合同冻结的签约人快照。</span>
              </div>
            </aside>

            <section className="contract-sign-content">
              {portal.isSign ? (
                <ContractSignSuccess portal={portal} />
              ) : (
                <>
                  <div className="contract-sign-section-heading">
                    <div>
                      <span>签约人身份核验</span>
                      <h1>确认并签署合同</h1>
                    </div>
                    {portal.isMainSigner ? <Tag color="arcoblue">主签约人</Tag> : null}
                  </div>

                  <div className="contract-sign-identity-preview">
                    <IdentityPreview label="姓名" value={portal.signerNameMasked} />
                    <IdentityPreview label="手机" value={portal.signerPhoneMasked} />
                    <IdentityPreview label="证件号" value={portal.signerCertificateNoMasked} />
                  </div>

                  {portal.blockedReason ? (
                    <Alert
                      className="contract-sign-inline-alert"
                      type="warning"
                      showIcon
                      content={portal.blockedReason}
                    />
                  ) : null}
                  {portal.providerActionType === "real_name" ? (
                    <Alert
                      className="contract-sign-inline-alert"
                      type="info"
                      showIcon
                      content="请先在法大大页面完成实名认证，完成后返回本页继续签署。"
                    />
                  ) : null}
                  {portal.providerActionType === "sign" ? (
                    <Alert
                      className="contract-sign-inline-alert"
                      type="success"
                      showIcon
                      content="实名认证已完成，请打开法大大签署页面完成签名。"
                    />
                  ) : null}
                  {signMutation.error ? (
                    <Alert
                      className="contract-sign-inline-alert"
                      type="error"
                      showIcon
                      content={getContractSignErrorMessage(signMutation.error)}
                    />
                  ) : null}

                  <Form<ContractSignFormValues>
                    form={form}
                    className="contract-sign-form"
                    layout="vertical"
                    initialValues={{ agreedToTerms: false }}
                    onSubmit={(values) => {
                      if (enteringContractPage) {
                        return;
                      }
                      signMutation.mutate({
                        signerName: values.signerName.trim(),
                        signerPhone: values.signerPhone.trim(),
                        signerCertificateNo: values.signerCertificateNo.trim(),
                        agreedToTerms: values.agreedToTerms,
                      });
                    }}
                  >
                    <Form.Item
                      field="signerName"
                      label="签约人姓名"
                      rules={[{ required: true, message: "请输入签约人姓名" }]}
                    >
                      <Input
                        aria-label="签约人姓名"
                        autoComplete="name"
                        disabled={enteringContractPage}
                        placeholder="请输入合同冻结的真实姓名"
                      />
                    </Form.Item>
                    <Form.Item
                      field="signerPhone"
                      label="签约手机号"
                      rules={[{ required: true, message: "请输入签约手机号" }]}
                    >
                      <Input
                        aria-label="签约手机号"
                        autoComplete="tel"
                        inputMode="tel"
                        disabled={enteringContractPage}
                        placeholder="请输入合同冻结的手机号"
                      />
                    </Form.Item>
                    <Form.Item
                      field="signerCertificateNo"
                      label="证件号码"
                      rules={[{ required: true, message: "请输入证件号码" }]}
                    >
                      <Input
                        aria-label="证件号码"
                        autoComplete="off"
                        disabled={enteringContractPage}
                        placeholder="请输入合同冻结的证件号码"
                      />
                    </Form.Item>

                    <div className="contract-sign-terms">
                      <div className="contract-sign-terms-title">
                        <LockKeyhole size={17} />
                        <span>签署确认</span>
                      </div>
                      <p>提交后将进入法大大实名认证或合同签署页面，签署结果由法大大回调同步。</p>
                      <Form.Item
                        field="agreedToTerms"
                        triggerPropName="checked"
                        rules={[
                          {
                            validator(value, callback) {
                              if (value === true) {
                                callback();
                                return;
                              }
                              callback("请先阅读并同意合同条款");
                            },
                          },
                        ]}
                      >
                        <Checkbox
                          aria-label="我已阅读并同意合同条款"
                          disabled={enteringContractPage}
                        >
                          我已阅读并同意合同条款，确认由本人完成本次签署
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <Button
                      className="contract-sign-submit"
                      type="primary"
                      htmlType="submit"
                      icon={<FileSignature size={17} />}
                      loading={enteringContractPage}
                      disabled={!portal.canSign || enteringContractPage}
                    >
                      确认签署
                    </Button>
                  </Form>
                </>
              )}
            </section>
          </div>
        ) : null}
      </main>

      <footer className="contract-sign-footer">
        <MessageSquareText size={16} />
        <span>签署通知由合同所选服务商渠道发出</span>
      </footer>
    </div>
  );
}

/** 渲染一项合同摘要。 */
function ContractSummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** 渲染签约人脱敏身份预览。 */
function IdentityPreview({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** 渲染合同签署成功结果。 */
function ContractSignSuccess({ portal }: { portal: OrderHandoverContractSignPortal }) {
  return (
    <div className="contract-sign-success" role="status">
      <CheckCircle2 size={52} />
      <span>签署结果已同步</span>
      <h1>合同签署完成</h1>
      <p>合同号 {portal.contractNo}</p>
      <div className="contract-sign-success-detail">
        <ContractSummaryItem label="签约人" value={portal.signerNameMasked} />
        <ContractSummaryItem label="签约服务" value={portal.providerName} />
        <ContractSummaryItem
          label="签署时间"
          value={formatContractSignTime(portal.signSuccessTime)}
        />
      </div>
    </div>
  );
}

/** 通知已打开的合同交接页刷新签署进度。 */
function notifyContractHandoverPage(portal: OrderHandoverContractSignPortal) {
  if (typeof BroadcastChannel === "undefined") {
    return;
  }
  const channel = new BroadcastChannel("order-handover-contract-signing");
  channel.postMessage({
    type: "order-handover-contract-signed",
    projectId: portal.customizationProjectId,
    signRecordId: portal.signRecordId,
  });
  channel.close();
}

/** 把签署异常转换为可展示消息。 */
function getContractSignErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "合同签署处理失败，请核对信息后重试。";
}

/** 格式化合同签署时间。 */
function formatContractSignTime(value: string | undefined): string {
  if (!value) {
    return "刚刚完成";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
    hour12: false,
  }).format(date);
}
